import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPracticeAreasOpen, setIsPracticeAreasOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const location = useLocation();

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const navigationItems = [
    {
      label: currentLanguage === 'en' ? 'Home' : 'Nyumbani',
      path: '/homepage',
      icon: 'Home'
    },
    {
      label: currentLanguage === 'en' ? 'Practice Areas' : 'Maeneo ya Utendaji',
      path: '/practice-areas-overview',
      icon: 'Scale',
      hasDropdown: true,
      dropdownItems: [
        {
          label: currentLanguage === 'en' ? 'Corporate Law' : 'Sheria ya Makampuni',
          path: '/individual-practice-area-detail?area=corporate-law',
          description: currentLanguage === 'en' ? 'Business formation, contracts, and compliance' : 'Uundaji wa biashara, mikataba, na kufuata sheria'
        },
        {
          label: currentLanguage === 'en' ? 'Family Law' : 'Sheria ya Familia',
          path: '/individual-practice-area-detail?area=family-law',
          description: currentLanguage === 'en' ? 'Divorce, custody, and matrimonial matters' : 'Talaka, ulinzi, na mambo ya ndoa'
        },
        {
          label: currentLanguage === 'en' ? 'Property Law' : 'Sheria ya Mali',
          path: '/individual-practice-area-detail?area=property-law',
          description: currentLanguage === 'en' ? 'Real estate transactions and disputes' : 'Miamala ya mali na migogoro'
        },
        {
          label: currentLanguage === 'en' ? 'Criminal Defense' : 'Utetezi wa Jinai',
          path: '/individual-practice-area-detail?area=criminal-defense',
          description: currentLanguage === 'en' ? 'Criminal representation and defense' : 'Uwakilishi na utetezi wa jinai'
        }
      ]
    },
    {
      label: currentLanguage === 'en' ? 'Our Team' : 'Timu Yetu',
      path: '/team-profiles',
      icon: 'Users'
    },
    {
      label: currentLanguage === 'en' ? 'About Us' : 'Kuhusu Sisi',
      path: '/about-us',
      icon: 'Info'
    },
    {
      label: currentLanguage === 'en' ? 'Contact' : 'Wasiliana',
      path: '/contact-us',
      icon: 'Phone'
    }
  ];

  const contactInfo = {
    phone: '+254 721 320 787',
    whatsapp: '+254721320787',
    email: 'info@peterpartnerslaw.co.ke'
  };

  const isActivePath = (path) => {
    if (path === '/homepage' && location.pathname === '/') return true;
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(currentLanguage === 'en' ?'Hello, I would like to inquire about your legal services.' :'Hujambo, ningependa kuuliza kuhusu huduma zenu za kisheria.');
    window.open(`https://wa.me/${contactInfo.whatsapp}?text=${message}`, '_blank');
  };

  const handleCallClick = () => {
    window.open(`tel:${contactInfo.phone}`, '_self');
  };

  const toggleLanguage = () => {
    const newLanguage = currentLanguage === 'en' ? 'sw' : 'en';
    setCurrentLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  return (
    <header className="sticky top-0 z-100 bg-background border-b border-border shadow-sm">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/homepage" className="flex items-center space-x-3 focus-ring rounded-lg">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg">
              <Icon name="Scale" size={24} color="white" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col">
              <span className="font-heading font-bold text-lg text-text-primary leading-tight">
                Peter Partners
              </span>
              <span className="font-caption text-xs text-text-secondary leading-tight">
                {currentLanguage === 'en' ? 'Law Firm' : 'Kampuni ya Sheria'}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <div key={item.path} className="relative">
                {item.hasDropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setIsPracticeAreasOpen(true)}
                    onMouseLeave={() => setIsPracticeAreasOpen(false)}
                  >
                    <Link
                      to={item.path}
                      className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 focus-ring ${
                        isActivePath(item.path)
                          ? 'text-primary bg-surface/20' :'text-text-primary hover:text-primary hover:bg-surface/10'
                      }`}
                    >
                      <span>{item.label}</span>
                      <Icon 
                        name="ChevronDown" 
                        size={16} 
                        className={`transition-transform duration-200 ${
                          isPracticeAreasOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </Link>

                    {/* Dropdown Menu */}
                    {isPracticeAreasOpen && (
                      <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-border z-200 animate-slide-down">
                        <div className="p-4 space-y-2">
                          {item.dropdownItems.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.path}
                              to={dropdownItem.path}
                              className="block p-3 rounded-lg hover:bg-surface/10 transition-colors duration-200 focus-ring"
                            >
                              <div className="font-medium text-text-primary text-sm">
                                {dropdownItem.label}
                              </div>
                              <div className="text-text-secondary text-xs mt-1 font-caption">
                                {dropdownItem.description}
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 focus-ring ${
                      isActivePath(item.path)
                        ? 'text-primary bg-surface/20' :'text-text-primary hover:text-primary hover:bg-surface/10'
                    }`}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop Contact Actions */}
          <div className="hidden lg:flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={toggleLanguage}
              className="text-text-secondary hover:text-primary"
            >
              {currentLanguage === 'en' ? 'SW' : 'EN'}
            </Button>
            
            <Button
              variant="outline"
              onClick={handleCallClick}
              iconName="Phone"
              iconPosition="left"
              size="sm"
            >
              {currentLanguage === 'en' ? 'Call' : 'Piga simu'}
            </Button>
            
            <Button
              variant="primary"
              onClick={handleWhatsAppClick}
              iconName="MessageCircle"
              iconPosition="left"
              size="sm"
            >
              WhatsApp
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-2">
            <Button
              variant="ghost"
              onClick={toggleLanguage}
              className="text-text-secondary hover:text-primary"
              size="sm"
            >
              {currentLanguage === 'en' ? 'SW' : 'EN'}
            </Button>
            
            <Button
              variant="ghost"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              iconName={isMenuOpen ? "X" : "Menu"}
              size="sm"
            />
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-border bg-white animate-slide-down">
            <div className="px-4 py-4 space-y-2">
              {navigationItems.map((item) => (
                <div key={item.path}>
                  {item.hasDropdown ? (
                    <div>
                      <button
                        onClick={() => setIsPracticeAreasOpen(!isPracticeAreasOpen)}
                        className={`w-full flex items-center justify-between px-3 py-3 rounded-lg text-left transition-colors duration-200 ${
                          isActivePath(item.path)
                            ? 'text-primary bg-surface/20' :'text-text-primary hover:bg-surface/10'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon name={item.icon} size={20} />
                          <span className="font-medium">{item.label}</span>
                        </div>
                        <Icon 
                          name="ChevronDown" 
                          size={16} 
                          className={`transition-transform duration-200 ${
                            isPracticeAreasOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      
                      {isPracticeAreasOpen && (
                        <div className="mt-2 ml-6 space-y-1">
                          {item.dropdownItems.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.path}
                              to={dropdownItem.path}
                              onClick={() => setIsMenuOpen(false)}
                              className="block px-3 py-2 rounded-lg text-sm text-text-secondary hover:text-primary hover:bg-surface/10 transition-colors duration-200"
                            >
                              {dropdownItem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      onClick={() => setIsMenuOpen(false)}
                      className={`flex items-center space-x-3 px-3 py-3 rounded-lg transition-colors duration-200 ${
                        isActivePath(item.path)
                          ? 'text-primary bg-surface/20' :'text-text-primary hover:bg-surface/10'
                      }`}
                    >
                      <Icon name={item.icon} size={20} />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Mobile Contact Actions */}
              <div className="pt-4 border-t border-border space-y-2">
                <Button
                  variant="outline"
                  onClick={handleCallClick}
                  iconName="Phone"
                  iconPosition="left"
                  fullWidth
                >
                  {contactInfo.phone}
                </Button>
                
                <Button
                  variant="primary"
                  onClick={handleWhatsAppClick}
                  iconName="MessageCircle"
                  iconPosition="left"
                  fullWidth
                >
                  {currentLanguage === 'en' ? 'WhatsApp Consultation' : 'Mazungumzo ya WhatsApp'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;